import { useState } from 'react';
import { ChevronLeft, Lock } from 'lucide-react';
import { CardBrandIcon, Input, Label } from '@/components/ui';
import { expectedCvvLength } from '@/lib/card-formatting';
import type { SavedCardInstrument } from '@/lib/paycom/types';
import { cardFieldClass, payButtonClass } from './paymentFormClasses';

interface Props {
  card: SavedCardInstrument;
  requireCVV: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: (cvv: string | null) => void;
  /** Visible label on the submit button — usually "Pay $X.XX". */
  submitLabel: string;
}

/**
 * Confirmation step for paying with a previously-saved card. Shows the
 * brand + last4 inline, optionally re-prompts for CVV when the saved
 * source doesn't have one cached.
 */
export function ExistingCardForm({ card, requireCVV, isSubmitting, onBack, onSubmit, submitLabel }: Props) {
  const [cvv, setCvv] = useState('');
  const need = expectedCvvLength(card.brand as 'amex' | 'visa' | 'mastercard' | 'discover' | 'unknown');

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    onSubmit(requireCVV ? cvv : null);
  }

  const canSubmit = !isSubmitting && (!requireCVV || cvv.length === need);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="-ml-1 flex items-center gap-1 rounded px-1 text-sm font-medium text-purple hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40"
        >
          <ChevronLeft className="size-4" strokeWidth={2.5} /> Back
        </button>
        <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-ink-muted">
          <Lock className="size-3" /> Encrypted
        </span>
      </div>

      <div className="flex items-center gap-3 rounded-[14px] border-2 border-purple bg-input-soft px-4 py-3">
        <CardBrandIcon brand={card.brand} size={36} />
        <div className="flex flex-col">
          <span className="text-sm font-semibold capitalize text-ink">{card.brand}</span>
          <span className="font-mono text-xs text-ink-muted tracking-wider">•••• {card.last4}</span>
        </div>
      </div>

      {requireCVV && (
        <div className="flex flex-col gap-1.5 max-w-32">
          <Label htmlFor="existing-cvv" className="text-sm text-ink-muted">
            CVV
          </Label>
          <Input
            id="existing-cvv"
            inputMode="numeric"
            autoComplete="cc-csc"
            maxLength={need}
            placeholder={card.brand === 'amex' ? '••••' : '•••'}
            value={cvv}
            onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
            className={`${cardFieldClass} font-mono`}
          />
          <p className="text-xs text-ink-muted">
            {need} digits on the {card.brand === 'amex' ? 'front' : 'back'} of the card.
          </p>
        </div>
      )}

      <button type="submit" disabled={!canSubmit} className={payButtonClass}>
        {isSubmitting ? 'Processing…' : submitLabel}
      </button>
    </form>
  );
}
